// Phase 18 — case-side entry point for copying a 000BIB PDF into the
// current case's configured caseLibraryCopySubfolder. Used by both
// CaseView (kind="case") and CollectiveCaseView (kind="cc").
//
// Listing: pulls only books with pdfFileName populated (the rest cannot
// be copied — the PDF flag without an attachment means the firm has it
// elsewhere). Multi-select + "Copy to this case" copies into the case
// folder; "Copy reference only" writes the formatted citation text to
// the clipboard (no insertion into the case). Same row-click selection
// drives both buttons.
//
// Phase 12 Stage 3 — embedded inside the shared CaseDocumentsSection's
// "Legal references" sub-pane. The parent owns the card chrome (.dc) and
// the pane title; this component renders only the search input, the two
// action buttons, and the results table.
//
// 200 §B [RULED — Laurent, at the 196/197 glance: "no point showing such a
// small window: make it a popup drawer with a big window then, like
// emails/messages drawer"] — THE PICKER IS A DRAWER. The pane keeps ONE
// control (« Open the library… ») where the cramped 320-px list stood, and
// the list, its search box and its two buttons open in `DrawerShell` — the
// shell the e-mails and messages drawers wear since 194D §0.5, full-width at
// DRAWER_Z, closed by its own Close and by Escape. ONE component, BOTH hosts:
// CaseView and CollectiveCaseView mount CaseDocumentsSection, which mounts
// this file once (CaseDocumentsSection.tsx, sub-pane (ii)); neither view has
// a spelling of its own. THE BEHAVIOUR IS UNTOUCHED (B.3): the one
// `GET /books` on mount, the PDF-bearing filter, the multi-select, what the
// two buttons copy and how — only the window changed. Closing the drawer
// clears the selection and the search, as the other two drawers open fresh
// per press.

import React, { useState, useEffect, useMemo } from "react";
import { api, copyBookToCase, copyBookToCollectiveCase } from "../api";
import { useTranslation } from "../i18n";
import { usePermissions } from "../hooks/usePermissions";
import { Search, FileText, Check, RefreshCw, Copy, LibraryBig } from "lucide-react";
import { FS, Z } from "../lib/tokens";
import Table from "./Table";
import { DrawerShell } from "./SharedUI";
import type { Translate } from "../lib/interestRefusal"; // the translator, narrowed once at the seam (199's rule)

// Build the citation text for clipboard. Mirrors LibraryView.jsx's
// copyReference format: non-empty fields joined with ", ".
function formatReference(b: any) {
  return [b.author, b.title, b.publisher, b.year]
    .map((v) => String(v ?? "").trim())
    .filter(Boolean)
    .join(", ");
}

export default function CaseLibraryCopySection({ caseId, kind, toast }: any) {
  // 200 §B — narrowed ONCE at the seam: `useTranslation().t` is `any`, and
  // every call off it costs the per-file ratchet two warnings (199 [METHOD] b).
  const { t } = useTranslation() as { t: Translate };
  const { can } = usePermissions();
  const scopeArg = kind === "cc" ? { ccId: caseId } : { caseId };
  const canSee =
    can("lateral.library.view") && can("caseView.documents.edit", scopeArg);

  const [books, setBooks] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState<Set<any>>(new Set());
  const [busy, setBusy] = useState(false);
  // 200 §B — the drawer; closing it clears the selection and the search.
  const [open, setOpen] = useState(false);
  const closeDrawer = () => {
    setOpen(false);
    setSelected(new Set());
    setQ("");
  };

  useEffect(() => {
    if (!canSee || books !== null || loading) return;
    setLoading(true);
    api
      .get("/books")
      .then((rows) => setBooks(Array.isArray(rows) ? rows : []))
      .catch((e) => {
        toast?.("⚠ " + ((e as any)?.message || t("caseLib.load_failed", { fallback: "Failed to load library" })));
        setBooks([]);
      })
      .finally(() => setLoading(false));
  }, [canSee, books, loading, toast]);

  const attachable = useMemo(() => {
    const rows = (books || []).filter((b: any) => b.pdfFileName);
    const ql = q.trim().toLowerCase();
    if (!ql) return rows;
    return rows.filter(
      (b: any) =>
        (b.author || "").toLowerCase().includes(ql) ||
        (b.title || "").toLowerCase().includes(ql) ||
        (b.publisher || "").toLowerCase().includes(ql) ||
        (b.year || "").toLowerCase().includes(ql) ||
        (b.code || "").toLowerCase().includes(ql),
    );
  }, [books, q]);

  const toggle = (id: any) => {
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  };

  // Copy the selected books' PDFs into the case's library subfolder.
  const doCopyToCase = async () => {
    if (!selected.size || busy) return;
    setBusy(true);
    let copied = 0,
      dup = 0,
      failed = 0;
    const callOne = kind === "cc" ? copyBookToCollectiveCase : copyBookToCase;
    for (const bookId of [...selected]) {
      try {
        const r = (await callOne(caseId, bookId)) as { status?: string } | null;
        if (r && r.status === "duplicate") dup++;
        else if (r && r.status === "copied") copied++;
        else failed++;
      } catch (e) {
        failed++;
        if (failed === 1) {
          const code = (e as any)?.code || "";
          const msg =
            code === "libraryCopy.no_subfolder"
              ? t("caseLib.no_subfolder", {
                  fallback:
                    "Admin has not configured a library-copy subfolder in Settings.",
                })
              : code === "libraryCopy.no_case_folder"
                ? t("caseLib.no_case_folder", {
                    fallback: "This case has no linked SharePoint folder.",
                  })
                : code === "m365.not_connected"
                  ? t("caseLib.m365_not_connected", {
                      fallback: "Microsoft 365 not connected — go to Settings.",
                    })
                  : (e as any)?.message ||
                    t("caseLib.copy_failed", { fallback: "Copy failed" });
          toast?.("⚠ " + msg);
        }
      }
    }
    setBusy(false);
    setSelected(new Set());
    if (copied || dup) {
      toast?.(
        // 209 §7 [RULED 16] — one sentence per number, each in its language's plural
        [
          t("caseLib.summary_copied", { count: copied, fallback: `${copied} copied` }),
          t("caseLib.summary_dup", { count: dup, fallback: `${dup} already there` }),
          t("caseLib.summary_failed", { count: failed, fallback: `${failed} failed` }),
        ].join(" · "),
      );
    }
  };

  // Copy just the citation text(s) to clipboard — no case-side write.
  // Mirrors LibraryView.jsx's per-row copyReference, batched for the
  // multi-selection here (one citation per line when multiple).
  const doCopyReferenceOnly = async () => {
    if (!selected.size) return;
    const lines: any[] = [];
    for (const bookId of selected) {
      const b = (books || []).find((x: any) => x.id === bookId);
      if (!b) continue;
      const txt = formatReference(b);
      if (txt) lines.push(txt);
    }
    if (!lines.length) return;
    const text = lines.join("\n");
    try {
      await navigator.clipboard.writeText(text);
      toast?.(t("library.reference_copied", { fallback: "Reference copied" }));
    } catch (e) {
      toast?.(
        "⚠ " +
          ((e as any)?.message || t("library.copy_failed", { fallback: "Copy failed" })),
      );
    }
  };

  if (!canSee) return null;

  // Phase 13 Item 1 — unified across the eight CaseView action buttons; style
  // is provided by `.btU.btUP/.btUS` in App.css. Locked dimensions are in the
  // class; this object is left empty so a future per-call override doesn't
  // crash but stops adding visual variance.
  const sharedBtnStyle = {};
  // Strip a trailing period from the empty text without forking the
  // existing i18n key — works against both the fallback string and any
  // DB-supplied catalog value.
  const emptyText = (
    t("caseLib.empty", { fallback: "No books with attached PDFs match" }) || ""
  ).replace(/\.\s*$/, "");
  // 200 §B — the drawer's OWN empty line: the library holds no book with a
  // PDF at all (nothing to copy, whatever is typed) — distinct from a search
  // that matches nothing, which keeps `caseLib.empty` above.
  const nothingCopyable =
    !loading && books !== null && !(books as { pdfFileName?: unknown }[]).some((b) => !!b.pdfFileName);
  const drawerTitle = t("caseLib.drawer_title", { fallback: "Copy from the library" });

  return (
    <>
      {/* THE PANE'S CONTROL — in place of the list (200 §B.2). */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <button
          type="button"
          className="btn-unified btn-unified-primary"
          style={sharedBtnStyle}
          onClick={() => setOpen(true)}
          title={drawerTitle}
        >
          <LibraryBig size={11} />
          {t("caseLib.drawer_open", { fallback: "Open the library…" })}
        </button>
      </div>

      {open && (
        <DrawerShell title={drawerTitle} ariaLabel={drawerTitle} onClose={closeDrawer}>
          {/* The search row — the drawers' own row dress (10px 14px, a rule below). */}
          <div style={{ padding: "10px 14px", borderBottom: "1px solid var(--br)" }}>
            <div style={{ position: "relative", maxWidth: 520 }}>
              <Search
                size={11}
                style={{
                  position: "absolute",
                  left: 7,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "var(--tm)",
                }}
              />
              <input
                className="field-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={t("caseLib.search_placeholder.library", {
                  fallback: "Search books and articles in Library…",
                })}
                style={{
                  margin: 0,
                  padding: "4px 8px 4px 22px",
                  fontSize: FS.s11,
                  width: "100%",
                  boxSizing: "border-box",
                }}
              />
            </div>
          </div>

          {/* The list — fills the drawer and scrolls; the header stays put. */}
          <div style={{ flex: "1 1 auto", minHeight: 0, overflowY: "auto", padding: "0 14px" }}>
            {loading && (
              <p
                style={{
                  fontSize: FS.s11,
                  color: "var(--tm)",
                  margin: "12px 0",
                  textAlign: "center",
                }}
              >
                {t("common.loading", { fallback: "Loading…" })}
              </p>
            )}
            {!loading && attachable.length === 0 && (
              <p
                style={{
                  fontSize: FS.s11,
                  color: "var(--tm)",
                  fontStyle: "italic",
                  margin: "12px 0",
                  textAlign: "center",
                }}
              >
                {nothingCopyable
                  ? t("caseLib.drawer_empty", {
                      fallback:
                        "The library holds no book with a PDF yet — attach one in the Library section to copy it here.",
                    })
                  : emptyText}
              </p>
            )}
            {!loading && attachable.length > 0 && (
              <Table style={{ width: "100%", fontSize: FS.s11 }}>
                <thead
                  style={{
                    position: "sticky",
                    top: 0,
                    background: "var(--bc)",
                    zIndex: Z.stickyHead,
                  }}
                >
                  <tr>
                    <th
                      style={{
                        width: 24,
                        textAlign: "center",
                        padding: "4px 6px",
                      }}
                    ></th>
                    <th style={{ textAlign: "left", padding: "4px 6px" }}>
                      {t("library.title", { fallback: "Title" })}
                    </th>
                    <th style={{ textAlign: "left", padding: "4px 6px" }}>
                      {t("library.author", { fallback: "Author" })}
                    </th>
                    <th
                      style={{
                        textAlign: "left",
                        padding: "4px 6px",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {t("library.year", { fallback: "Year" })}
                    </th>
                    <th style={{ width: 28, padding: "4px 6px" }}></th>
                  </tr>
                </thead>
                <tbody>
                  {attachable.slice(0, 200).map((b: any) => {
                    const sel = selected.has(b.id);
                    return (
                      <tr
                        key={b.id}
                        style={{
                          cursor: "pointer",
                          background: sel ? "rgba(74,124,255,.07)" : undefined,
                        }}
                        onClick={() => toggle(b.id)}
                      >
                        <td
                          style={{ textAlign: "center", padding: "3px 6px" }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <input
                            type="checkbox"
                            checked={sel}
                            onChange={() => toggle(b.id)}
                          />
                        </td>
                        <td style={{ padding: "3px 6px" }}>{b.title || "—"}</td>
                        <td style={{ padding: "3px 6px", color: "var(--tm)" }}>
                          {b.author || "—"}
                        </td>
                        <td
                          style={{
                            padding: "3px 6px",
                            color: "var(--tm)",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {b.year || "—"}
                        </td>
                        <td
                          style={{ padding: "3px 6px", textAlign: "right" }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {b.pdfWebUrl ? (
                            <a
                              href={b.pdfWebUrl}
                              target="_blank"
                              rel="noopener"
                              style={{ color: "var(--gn)" }}
                              title={b.pdfFileName}
                            >
                              <FileText size={12} />
                            </a>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            )}
          </div>

          {/* The two actions, on the drawer's foot — the same buttons, the same handlers. */}
          <div
            style={{
              padding: "10px 14px",
              borderTop: "1px solid var(--br)",
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            <button
              type="button"
              className="btn-unified btn-unified-primary"
              style={sharedBtnStyle}
              onClick={doCopyToCase}
              disabled={!selected.size || busy}
              title={t("caseLib.copy_to_case.title", {
                fallback: "Copy selected books into this case folder",
              })}
            >
              {busy ? (
                <RefreshCw size={11} className="spin" />
              ) : (
                <Check size={11} />
              )}
              {t("caseLib.copy_to_case", { fallback: "Copy to this case" })}
            </button>
            <button
              type="button"
              className="btn-unified btn-unified-primary"
              style={sharedBtnStyle}
              onClick={doCopyReferenceOnly}
              disabled={!selected.size}
              title={t("caseLib.copy_reference_only.title", {
                fallback: "Copy the citation text to clipboard",
              })}
            >
              <Copy size={11} />
              {t("caseLib.copy_reference_only", {
                fallback: "Copy reference only",
              })}
            </button>
            {selected.size > 0 && (
              <span style={{ fontSize: FS.s10, color: "var(--ac)" }}>
                {selected.size} {t("common.selected", { count: selected.size, fallback: "selected" })}
              </span>
            )}
          </div>
        </DrawerShell>
      )}
    </>
  );
}
